import React, { useState, useRef, useEffect } from 'react';
import Input from 'components/ui/Input';
import Icon from 'components/AppIcon';

const LocationSection = ({ locationName, onChange, errors }) => {
  const [searchQuery, setSearchQuery] = useState(locationName || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef(null);
  const wrapperRef = useRef(null);

  // Mapbox token with fallback for development
  const MAPBOX_TOKEN = (() => {
    const envToken = import.meta.env?.VITE_MAPBOX_TOKEN;
    if (envToken && envToken !== 'your-mapbox-token-here' && !envToken?.includes('placeholder')) {
      return envToken;
    }
    return 'pk.eyJ1IjoicGVkZXJ6b2xsaXNpbW9uZSIsImEiOiJjbWt5OTA3OHowNG02M2VzZTFoYWJwN3E2In0.HCcTrRyl9L0dWbcIp0x1iQ';
  })();

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef?.current && !wrapperRef?.current?.contains(event?.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search function using Mapbox Geocoding API
  const searchLocations = async (query) => {
    if (!query || query?.length < 2) {
      setSuggestions([]);
      return;
    }

    if (!MAPBOX_TOKEN) {
      console.error('Mapbox token not found');
      return;
    }

    setIsLoading(true);

    try {
      // Use Geocoding API v5 with place types to prevent street-level addresses
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?country=IT&types=country,region,place,locality&language=it&limit=5&access_token=${MAPBOX_TOKEN}`;
      
      const response = await fetch(url);

      if (!response?.ok) {
        throw new Error(`Mapbox API returned ${response.status}`);
      }

      const data = await response?.json();
      
      // Transform Geocoding API response to match our UI expectations
      const transformedSuggestions = data?.features?.map(feature => ({
        mapbox_id: feature?.id,
        name: feature?.place_name,
        place_formatted: feature?.place_name,
        context: feature?.context || [],
        coordinates: feature?.center
      })) || [];
      
      setSuggestions(transformedSuggestions);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching location suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const value = e?.target?.value;
    setSearchQuery(value);
    onChange('locationName', value);

    if (searchTimeoutRef?.current) {
      clearTimeout(searchTimeoutRef?.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchLocations(value);
    }, 300);
  };

  // Handle location selection
  const handleSelectLocation = (suggestion) => {
    setSearchQuery(suggestion?.name || '');
    onChange('locationName', suggestion?.name || '');
    
    // Store mapbox_id for database persistence
    onChange('locationMapboxId', suggestion?.mapbox_id || '');
    
    // Store additional location data if needed
    onChange('locationData', {
      name: suggestion?.name,
      coordinates: suggestion?.coordinates,
      mapbox_id: suggestion?.mapbox_id
    });
    
    setShowSuggestions(false);
    setSuggestions([]);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
          <Icon name="MapPin" size={20} color="#10b981" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Dove Serve il Servizio?</h2>
          <p className="text-sm text-muted-foreground">Specifica la località per il servizio</p>
        </div>
      </div>

      <div className="space-y-4" ref={wrapperRef}>
        <div className="relative">
          <Input
            label="Città o Località"
            placeholder="Es: Milano, Roma, Torino"
            value={searchQuery}
            onChange={handleSearchChange}
            error={errors?.locationName}
            required
            description="Inserisci la città dove necessiti del servizio"
          />

          {/* Loading indicator */}
          {isLoading && (
            <div className="absolute right-3 top-9">
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {/* Suggestions dropdown */}
          {showSuggestions && suggestions?.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
              {suggestions?.map((suggestion, index) => (
                <button
                  key={suggestion?.mapbox_id || index}
                  type="button"
                  onClick={() => handleSelectLocation(suggestion)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-start gap-2">
                    <Icon name="MapPin" size={16} color="#6b7280" className="mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-sm text-foreground">
                        {suggestion?.name}
                      </div>
                      {suggestion?.place_formatted && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {suggestion?.place_formatted}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* No results message */}
          {showSuggestions && !isLoading && suggestions?.length === 0 && searchQuery?.length >= 2 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-4 text-center text-sm text-muted-foreground">
              Nessuna località trovata
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationSection;